import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Copy, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CopyableReference } from "@/components/ui/copyable-reference";
import { cn } from "@/lib/utils";
import { categoryForReasonCode, PRIMARY_ACTION_BY_CATEGORY, type PrimaryActionKind } from "@/i18n/error-categories";
import { formatCopyDetailsBlock, normalizeDiagnosticPath, normalizeDiagnosticTimestamp, normalizeReasonCode, normalizeSupportReference, type ErrorPageSource } from "@/i18n/problem-detail";
import { copyToClipboard, copyableReferenceLabels } from "@/i18n/copyable-reference";
import { useErrorLocale } from "@/i18n/use-error-locale";
import { referrerReturnPath, signInPathFor } from "@/lib/session-expiry";

/**
 * Branded, internationalised error page (ALL-4857).
 *
 * Renders for:
 *  - the /error route, reached via a browser 302 redirect from an auth
 *    failure (`?code=<AuthReasonCode>&ref=<supportReference>`)
 *  - the route-level error boundary, for an unexpected render-tree crash
 *  - any inline (non-navigation) API error, via the ProblemDetail mapping
 *
 * Accessibility: heading receives focus on mount (route-entry focus
 * management for screen readers/keyboard users navigating via redirect),
 * uses a single <h1>, and all spacing uses logical properties (ps-/pe-/ms-/me-
 * via Tailwind's built-in RTL-aware utilities) so the `ar` locale renders
 * correctly right-to-left without a separate layout.
 */
export function ErrorPage({ source: rawSource }: { source: ErrorPageSource }) {
  // An empty code means "we do not know why", not "say nothing": the category
  // is chosen from the code, so a browser redirect that carries `?code=` with
  // no value (or a ProblemDetail with a blank reasonCode) would otherwise fall
  // through to the wrong copy and copy a details block whose code line is
  // empty. An empty reasonCode gets the same explicit unknown-client-error
  // code as an absent one. `supportReference` needs no equivalent — an empty
  // reference is already treated as absent (no block, no line, no
  // placeholder: D636).
  const source: ErrorPageSource = {
    ...rawSource,
    reasonCode: normalizeReasonCode(rawSource.reasonCode),
    supportReference: normalizeSupportReference(rawSource.supportReference),
    timestamp: normalizeDiagnosticTimestamp(rawSource.timestamp),
    path: normalizeDiagnosticPath(rawSource.path) ?? undefined,
  };
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const catalog = useErrorLocale(searchParams);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [copiedDetails, setCopiedDetails] = useState(false);
  const referenceLabels = useMemo(() => copyableReferenceLabels(catalog.ui), [catalog]);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const category = categoryForReasonCode(source.reasonCode, source.httpStatus ?? undefined);
  const copy = catalog.categories[category] ?? catalog.categories.generic_4xx;
  const actionKind: PrimaryActionKind = PRIMARY_ACTION_BY_CATEGORY[category];

  const detailsBlock = formatCopyDetailsBlock(source, {
    codeLabel: catalog.ui.codeLabel,
    referenceLabel: catalog.ui.referenceLabel,
    timestampLabel: catalog.ui.timestampLabel,
    pathLabel: catalog.ui.pathLabel,
  });

  // The one shared copy routine (design-system copyable-reference.ts), which
  // falls back to execCommand when the Clipboard API is unavailable.
  async function copyText(text: string, onCopied: (v: boolean) => void) {
    if (!(await copyToClipboard(text))) return;
    onCopied(true);
    setTimeout(() => onCopied(false), 2000);
  }

  function handlePrimaryAction() {
    if (actionKind === "retry") {
      window.location.reload();
      return;
    }
    if (actionKind === "sign_in_again") {
      // ALL-5774: come back to the page that linked here after signing in.
      navigate(signInPathFor(referrerReturnPath()), { replace: true });
      return;
    }
    if (actionKind === "go_home") {
      navigate("/home", { replace: true });
      return;
    }
    // contact_support — no in-app support inbox in this environment yet;
    // surface the reference so the person can paste it into their existing
    // support channel rather than pretending we have a ticketing flow.
    void copyText(detailsBlock, setCopiedDetails);
  }

  const actionLabel =
    actionKind === "retry"
      ? catalog.ui.actionRetry
      : actionKind === "sign_in_again"
        ? catalog.ui.actionSignInAgain
        : actionKind === "go_home"
          ? catalog.ui.actionGoHome
          : catalog.ui.actionContactSupport;

  return (
    <main
      dir={catalog.dir}
      className="flex min-h-screen w-full items-center justify-center bg-bg p-4 sm:p-6"
    >
      {/* Softer padding on a phone (`p-4`/`px-4`, back to the 24px scale from
          `sm`): at 375px the desktop pair left a ~279px content column, which
          the mono reason code and the copy control below both needed. */}
      <div role="alert" className="w-full max-w-[560px] rounded-lg border border-line bg-surface px-4 py-10 text-center sm:px-6">
        <div className="mx-auto mb-4 grid size-14 place-items-center rounded-full border border-danger-border bg-danger-soft">
          <AlertTriangle aria-hidden className="size-7 text-danger" />
        </div>
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-xl font-extrabold text-ink focus-visible:outline-none"
        >
          {copy.title}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted">{copy.message}</p>
        {/* invite_required (AUTH_INVITE_REQUIRED): "administrators were told". The same sentence for
            every invite-required refusal, whether or not a request was recorded (capped, repeat or
            first), so the page reveals nothing about state (specs/access-requests S-12, ALL-5723). */}
        {copy.notice && (
          <p data-testid="error-page-notice" className="mt-2 text-sm leading-relaxed text-muted">{copy.notice}</p>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button variant="primary" onClick={handlePrimaryAction}>
            {actionLabel}
          </Button>
        </div>

        {/* Prominent, copyable details block — founder direction: the
            support reference must be visible and easy to copy. */}
        <div className="mt-8 rounded-md border border-line bg-bg p-4 text-start">
          {/* Shared CopyableReference (full variant). Left out entirely when
              the redirect or ProblemDetail carried no reference. */}
          <CopyableReference reference={source.supportReference} labels={referenceLabels} />

          {/* ALL-6426 / SUP-128: invalid auth context is explained by the
              branded sign-in copy above. Keep its internal code in copied
              support diagnostics only, never in the rendered page. */}
          {source.reasonCode !== "AUTH_CONTEXT_INVALID" && (
            <div className="grid grid-cols-[auto_1fr] gap-x-3 text-xs">
              <span className="text-muted">{catalog.ui.codeLabel}</span>
              <span dir="ltr" className="min-w-0 break-all font-mono text-ink">{source.reasonCode}</span>
            </div>
          )}

          {(source.timestamp || source.path) && (
            <div className={cn("grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs", source.supportReference && "mt-4")}>
              {source.timestamp && (
                <>
                  <span className="text-muted">{catalog.ui.timestampLabel}</span>
                  <span className="min-w-0 break-all font-mono text-ink">{source.timestamp}</span>
                </>
              )}
            {source.path && (
              <>
                <span className="text-muted">{catalog.ui.pathLabel}</span>
                <span className="break-all font-mono text-ink">{source.path}</span>
              </>
            )}
            </div>
          )}

          <button
            type="button"
            onClick={() => void copyText(detailsBlock, setCopiedDetails)}
            className={cn(
              "mt-4 inline-flex min-h-[36px] w-full items-center justify-center gap-1.5 rounded-sm border border-line px-3 py-2 text-xs font-semibold text-ink hover:border-primary",
            )}
          >
            {copiedDetails ? <Check aria-hidden className="size-3.5" /> : <Copy aria-hidden className="size-3.5" />}
            {copiedDetails ? catalog.ui.copyDetailsCopied : catalog.ui.copyDetails}
          </button>
          <p className="mt-2 text-[11px] leading-relaxed text-muted">{catalog.ui.supportHint}</p>
        </div>
      </div>
    </main>
  );
}
